import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import { Spinner } from "./Spinner";

/* The Project stage filter, in Buzze's words.
 *
 * The engine stores a project's stage as one of its own statuses and the
 * sales team thinks in six words (engine migrations 153 and 158,
 * api.stage_word). A campaign still stores statuses in project_status_in,
 * so a saved campaign needs no change and candidacy reads what it always
 * did. This control shows the words, and choosing one sets every status
 * under it. "Existing property" is a property with no project, which the
 * Already built filter (standing_stock) selects, so it is not offered
 * here. */
type StageWord = { status: string | null; word: string | null; word_order: number | null };

export function useStageWords(token: string) {
  return useQuery({
    queryKey: ["stage-words"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("stage_word")
        .select("status,word,word_order").order("word_order").order("status").limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      const words: { word: string; statuses: string[] }[] = [];
      for (const r of (data ?? []) as StageWord[]) {
        if (!r.word || !r.status || r.status === "existing") continue;
        const w = words.find((x) => x.word === r.word);
        if (w) w.statuses.push(r.status); else words.push({ word: r.word, statuses: [r.status] });
      }
      return words;
    },
  });
}

export function StageWordsControl({ token, value, onChange }: {
  token: string;
  value: string[];
  onChange: (statuses: string[] | undefined) => void;
}) {
  const words = useStageWords(token);
  if (words.isError) return <p className="refusal">{(words.error as Error).message}</p>;
  if (words.isLoading || !words.data) return <p className="muted"><Spinner />Loading.</p>;
  const on = (w: { statuses: string[] }) => w.statuses.every((s) => value.includes(s));
  return (
    <div className="checks">
      {words.data.map((w) => (
        <label key={w.word} className="check">
          <input type="checkbox" checked={on(w)} onChange={() => {
            const next = on(w)
              ? value.filter((s) => !w.statuses.includes(s))
              : Array.from(new Set([...value, ...w.statuses]));
            onChange(next.length ? next : undefined);
          }} />
          {" "}{w.word}
        </label>
      ))}
    </div>
  );
}
